// Shared heatmap renderer — one visual frame for both the Trends page heatmap and the Coaching
// "Recent Trends" heatmap, so they look identical. Rounded, softly-glowing cells in a 3-tier palette
// (at/above expected, just below, below expected) plus a neutral "not scored" hatch. A CSS grid so it
// handles any column count and an optional emphasised current column (the "This" call).
//
// The single source of truth for how a cell is coloured is heatTier(): score vs its expected minimum.
// Both pages feed rows of { value, threshold } and get the same banding — no per-page colour logic.

export type HeatTier = 'above' | 'near' | 'below' | 'na';

export interface HeatColumn {
  label: string;
  title?: string;
  isCurrent?: boolean; // emphasise this column (e.g. the current "This" call)
}
export interface HeatCell {
  value: number | null; // 0–5 observed score; null → not scored / no calls
  threshold: number | null; // expected minimum for this skill; null → no minimum on record
  title?: string; // native tooltip
}
export interface HeatRow {
  label: string;
  title?: string;
  cells: HeatCell[]; // aligned to columns
}
export interface HeatmapConfig {
  columns: HeatColumn[];
  rows: HeatRow[];
  skillHeader?: string; // top-left corner label (default blank)
  minColWidth?: number; // px per data column (default 44)
  showLegend?: boolean; // default true
}

/** The shared banding: a score vs its expected minimum. At/above → green; within 0.5 below → amber
 *  "just below"; further below → red; missing score or minimum → neutral. */
export function heatTier(value: number | null, threshold: number | null): HeatTier {
  if (value == null || threshold == null || !Number.isFinite(value) || !Number.isFinite(threshold)) return 'na';
  const d = value - threshold;
  if (d >= 0) return 'above';
  if (d >= -0.5) return 'near';
  return 'below';
}

const TIERS: { tier: HeatTier; label: string }[] = [
  { tier: 'above', label: 'at / above expected' },
  { tier: 'near', label: 'just below' },
  { tier: 'below', label: 'below expected' },
  { tier: 'na', label: 'not scored' },
];

function el(tag: string, cls: string, text?: string): HTMLElement {
  const e = document.createElement(tag);
  e.className = cls;
  if (text != null) e.textContent = text;
  return e;
}

function legend(): HTMLElement {
  const l = el('div', 'hm-legend');
  for (const { tier, label } of TIERS) {
    const item = el('span', 'hm-legend-item');
    item.appendChild(el('i', `hm-sw hm-${tier}`));
    item.appendChild(document.createTextNode(label));
    l.appendChild(item);
  }
  return l;
}

/** Build the heatmap element (a scroll wrapper containing the grid + legend). */
export function renderHeatmap(config: HeatmapConfig): HTMLElement {
  const wrap = el('div', 'hm-wrap');
  const scroll = el('div', 'hm-scroll');
  const grid = el('div', 'hm-grid');
  grid.style.gridTemplateColumns = `minmax(96px, max-content) repeat(${config.columns.length}, minmax(${config.minColWidth ?? 44}px, 1fr))`;

  // Header row: blank corner + one header per column.
  grid.appendChild(el('div', 'hm-corner', config.skillHeader ?? ''));
  for (const c of config.columns) {
    const chh = el('div', 'hm-colh' + (c.isCurrent ? ' is-current' : ''), c.label);
    if (c.title) chh.title = c.title;
    grid.appendChild(chh);
  }

  // One row per skill.
  for (const row of config.rows) {
    const rh = el('div', 'hm-rowh', row.label);
    if (row.title) rh.title = row.title;
    grid.appendChild(rh);
    row.cells.forEach((cell, i) => {
      const tier = heatTier(cell.value, cell.threshold);
      const current = config.columns[i]?.isCurrent ? ' is-current' : '';
      const c = el('div', `hm-cell hm-${tier}${current}`);
      c.textContent = cell.value != null && Number.isFinite(cell.value) ? cell.value.toFixed(1) : '';
      if (cell.title) c.title = cell.title;
      grid.appendChild(c);
    });
  }

  scroll.appendChild(grid);
  wrap.appendChild(scroll);
  if (config.showLegend !== false) wrap.appendChild(legend());
  return wrap;
}
